import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, Package, Wifi, WifiOff } from 'lucide-react';

// Users who can see dashboard and app selection
const DASHBOARD_ACCESS_USERS = ['alma', 'jelyn', 'nicole', 'carla', 'kristine', 'pabs'];

const AppSelect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // All authenticated users can access both NAS and ARBE
  const canAccessNAS = true;
  
  const usernameLower = user?.username?.toLowerCase() || '';
  const fullNameLower = user?.full_name?.toLowerCase().replace(/\s+/g, '') || '';
  
  // All users have dashboard access
  const hasDashboardAccess = true;

  // Redirect users without any access directly to timesheet
  // Users with NAS access should stay on this page to choose their app
  useEffect(() => {
    if (user && !hasDashboardAccess) {
      console.log('AppSelect: Redirecting to timesheet (no dashboard access)');
      navigate('/arbe/timesheet', { replace: true });
    }
  }, [user, hasDashboardAccess, navigate]);
  
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSelectApp = (app) => {
    localStorage.setItem('selectedApp', app);
    if (app === 'arbe') {
      navigate('/arbe/dashboard');
    } else {
      navigate('/nas/dashboard');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Online/Offline Indicator */}
      <div className={`fixed top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
        isOnline ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}>
        {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
        {isOnline ? 'Online' : 'Offline'}
      </div>
      
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white mb-2">Welcome, {user?.full_name || user?.username}!</h1>
          <p className="text-slate-400 text-lg">Select an application to continue</p>
        </div>
        
        {/* App Selection Cards */}
        <div className={`grid gap-6 ${canAccessNAS ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto'}`}>
          {/* ARBE Card */}
          <Card 
            className="bg-slate-800/50 border-slate-700 hover:border-blue-500 hover:bg-slate-800 transition-all cursor-pointer group"
            onClick={() => handleSelectApp('arbe')}
            data-testid="app-select-arbe"
          >
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-2xl bg-blue-600/20 flex items-center justify-center mb-4 group-hover:bg-blue-600/30 transition-colors">
                  <Building2 size={40} className="text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">ARBE</h2>
                <p className="text-slate-400 mb-4">Mipay Business Management</p>
                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Payroll</span>
                  <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Timesheet</span>
                  <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Cash Advance</span>
                  <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Subscribers</span>
                </div>
                <button className="mt-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                  Enter ARBE →
                </button>
              </div>
            </CardContent>
          </Card>
          
          {/* NAS Card - Only shown if user has access */}
          {canAccessNAS && (
            <Card 
              className="bg-slate-800/50 border-slate-700 hover:border-green-500 hover:bg-slate-800 transition-all cursor-pointer group"
              onClick={() => handleSelectApp('nas')}
              data-testid="app-select-nas"
            >
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-2xl bg-green-600/20 flex items-center justify-center mb-4 group-hover:bg-green-600/30 transition-colors">
                    <Package size={40} className="text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">NAS</h2>
                  <p className="text-slate-400 mb-4">Nenen's Agrivet Supply</p>
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Inventory</span>
                    <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Orders</span>
                    <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Bodega</span>
                    <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">Sales</span>
                  </div>
                  <button className="mt-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors">
                    Enter NAS →
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Footer */}
        <p className="text-center text-slate-500 mt-8 text-sm">
          Your data syncs automatically when online
        </p>
      </div>
    </div>
  );
};

export default AppSelect;
